/**
 * InstructionsBlock — order-level delivery instructions.
 *
 * Two surfaces:
 *  1. Admin (inside OrderDetailDrawer right column, below Tuning Files):
 *     • textarea for free-form text (saved on blur via PUT)
 *     • up to 3 attachments (PDF / JPG / PNG / WebP, 8 MB each)
 *     • inline thumbnails with delete + download buttons
 *
 *  2. Customer (only when order.status === 'completed'):
 *     • compact strip "Instructions for order" with file count + chevron
 *     • clicking opens a centered full-viewport modal with
 *       backdrop-blur showing the rendered text, file previews
 *       (PDFs in <iframe>, images zoomable inline), and fallback
 *       option-catalogue descriptions + Photo Library matches
 *       when the admin did not supply custom instructions.
 *
 * Backed by:
 *  GET    /api/orders/{id}/instructions
 *  PUT    /api/orders/{id}/instructions       { text }
 *  POST   /api/orders/{id}/instructions/files (multipart)
 *  DELETE /api/orders/{id}/instructions/files/{file_id}
 *  GET    /api/orders/{id}/instructions/files/{file_id}  (inline)
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader2, Save, Upload, Trash2, FileText, Image as ImageIcon, X, ChevronRight, BookOpen, FileType2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILES = 3;

function bytesPretty(n) {
  if (!n) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mime) {
  if ((mime || '').startsWith('image/')) return ImageIcon;
  if (mime === 'application/pdf') return FileType2;
  return FileText;
}

export default function InstructionsBlock({ token, order, isAdmin, isCustomer, embedded = false, noMargin = false, className = '' }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [data, setData]       = useState(null);   // { text, files, auto_blocks, has_custom, is_completed }
  const [textDraft, setTextDraft] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  // Admin starts COLLAPSED — the editor opens only when the operator
  // actively wants to add/edit a custom note. Most orders rely on the
  // auto-fallback (option descriptions + Photo Library) and never need
  // a custom note, so the full editor was wasting vertical space.
  const [adminExpanded, setAdminExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/orders/${order.id}/instructions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setData(j);
      setTextDraft(j.text || '');
    } catch (e) {
      console.error('instructions load failed', e);
    } finally { setLoading(false); }
  }, [order.id, token]);

  useEffect(() => { load(); }, [load]);

  const saveText = async () => {
    if (textDraft === (data?.text || '')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/orders/${order.id}/instructions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: textDraft }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Instruction text saved.');
      await load();
    } catch (e) { toast.error(`Save failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await fetch(`${API_URL}/api/orders/${order.id}/instructions/files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.detail || `HTTP ${r.status}`);
      toast.success(`Attached ${file.name}.`);
      await load();
    } catch (e) { toast.error(`Upload failed: ${e.message}`); }
    finally { setBusy(false); e.target.value = ''; }
  };

  const deleteFile = async (fileId, filename) => {
    if (!window.confirm(`Delete attachment "${filename}"?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/orders/${order.id}/instructions/files/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Attachment removed.');
      await load();
    } catch (e) { toast.error(`Delete failed: ${e.message}`); }
    finally { setBusy(false); }
  };

  // Fetch each attachment as an authenticated blob and expose
  // it as an `blob:` URL so <img>/<iframe> can render without
  // smuggling the JWT through query strings. URLs are revoked
  // on unmount / data reload to avoid memory leaks.
  const [blobUrls, setBlobUrls] = useState({}); // { file_id: blob: URL }
  useEffect(() => {
    let cancelled = false;
    const next = {};
    (async () => {
      for (const f of (data?.files || [])) {
        if (cancelled) return;
        try {
          const r = await fetch(`${API_URL}/api/orders/${order.id}/instructions/files/${f.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!r.ok) continue;
          const blob = await r.blob();
          next[f.id] = URL.createObjectURL(blob);
        } catch (_e) { /* best-effort */ }
      }
      if (!cancelled) setBlobUrls(next);
    })();
    return () => {
      cancelled = true;
      Object.values(next).forEach((u) => { try { URL.revokeObjectURL(u); } catch (_e) { /* ignore */ } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.files?.map((f) => f.id).join(',')]);

  const fileUrl = useCallback((fileId) => blobUrls[fileId] || '', [blobUrls]);

  // Customer view never renders the block until the order is completed
  // AND there is something to show (admin text/files OR auto-blocks).
  const hasAnything = useMemo(() => {
    if (!data) return false;
    return Boolean(
      (data.text && data.text.trim()) ||
      (data.files && data.files.length) ||
      (data.auto_blocks && data.auto_blocks.length),
    );
  }, [data]);

  if (loading) {
    if (embedded) {
      return (
        <div className="pt-3 mt-3 border-t border-slate-200" data-testid="instructions-loading">
          <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading instructions…</div>
        </div>
      );
    }
    return (
      <section className="bg-white rounded-xl border border-slate-200 p-4 mt-3 shadow-sm" data-testid="instructions-loading">
        <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading instructions…</div>
      </section>
    );
  }

  // ─── CUSTOMER SURFACE ─────────────────────────────────────────
  if (isCustomer) {
    if (!data?.is_completed || !hasAnything) return null;

    // Inline preview rules:
    //   • Show the operator's note INLINE if it's short (≤ 180 chars
    //     and at most 2 paragraphs) — saves the customer a click for
    //     simple instructions like "Flash via KESS3 only".
    //   • Always show small chips for image / PDF attachments so the
    //     customer sees at a glance what types of files are waiting
    //     inside before tapping.
    //   • Long notes (> 180 chars / multi-paragraph) collapse to a
    //     one-line summary with "Show more →" CTA.
    const PREVIEW_LIMIT = 180;
    const noteRaw   = (data.text || '').trim();
    const isShort   = noteRaw && noteRaw.length <= PREVIEW_LIMIT && noteRaw.split(/\n{2,}/).length === 1;
    const filesList = data.files || [];
    const imgCount  = filesList.filter(f => (f.mime || '').startsWith('image/')).length;
    const pdfCount  = filesList.filter(f => f.mime === 'application/pdf').length;
    const otherCnt  = filesList.length - imgCount - pdfCount;

    const FileChips = (filesList.length === 0) ? null : (
      <span className="inline-flex items-center gap-1.5 flex-wrap">
        {imgCount > 0 && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-bold" title={`${imgCount} image${imgCount>1?'s':''} attached`}>
            <ImageIcon className="w-2.5 h-2.5" />{imgCount}
          </span>
        )}
        {pdfCount > 0 && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 text-[10px] font-bold" title={`${pdfCount} PDF${pdfCount>1?'s':''} attached`}>
            <FileType2 className="w-2.5 h-2.5" />{pdfCount}
          </span>
        )}
        {otherCnt > 0 && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-bold">
            <FileText className="w-2.5 h-2.5" />{otherCnt}
          </span>
        )}
      </span>
    );

    if (embedded) {
      return (
        <>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="instructions-strip-customer"
            className="mt-3 pt-3 border-t border-slate-200 w-full flex items-start gap-2.5 text-left group"
          >
            <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-sm flex-shrink-0">
              <BookOpen className="w-3.5 h-3.5 text-white" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-bold text-slate-800">Instructions for this order</span>
              {isShort ? (
                <span className="block text-[12px] text-slate-700 leading-snug mt-0.5 whitespace-pre-wrap" data-testid="instructions-inline-text">{noteRaw}</span>
              ) : (
                <span className="block text-[11px] text-slate-500 truncate">
                  {data.has_custom
                    ? <>Custom note from operator{filesList.length ? ` · ${filesList.length} attachment${filesList.length > 1 ? 's' : ''}` : ''}</>
                    : <>Photo guide for {data.auto_blocks?.length || 0} option{(data.auto_blocks?.length || 0) === 1 ? '' : 's'}</>}
                </span>
              )}
              <span className="flex items-center gap-2 mt-1 flex-wrap">
                {FileChips}
                <span className="text-[10px] text-indigo-600 font-semibold">
                  {isShort && filesList.length === 0 ? 'Show more →' : 'Tap to open →'}
                </span>
              </span>
            </span>
            <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 group-hover:text-indigo-500 transition mt-1" />
          </button>

          {modalOpen && (
            <InstructionsModal
              data={data}
              order={order}
              fileUrl={fileUrl}
              onClose={() => setModalOpen(false)}
            />
          )}
        </>
      );
    }

    return (
      <>
        <section
          className={`bg-gradient-to-br from-indigo-50 via-white to-cyan-50 border border-indigo-200 rounded-xl p-3 shadow-sm hover:shadow-md transition cursor-pointer h-full ${noMargin ? '' : 'mt-3'} ${className}`}
          onClick={() => setModalOpen(true)}
          data-testid="instructions-strip-customer"
        >
          <div className="flex items-start gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-sm flex-shrink-0">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-slate-800 text-sm">Instructions for this order</h4>
              {isShort ? (
                <p className="text-[12px] text-slate-700 leading-snug mt-0.5 whitespace-pre-wrap" data-testid="instructions-inline-text">{noteRaw}</p>
              ) : (
                <p className="text-[11px] text-slate-500 leading-snug">
                  {data.has_custom
                    ? <>Custom note{filesList.length ? ` · ${filesList.length} attachment${filesList.length > 1 ? 's' : ''}` : ''}.</>
                    : <>Photo guide for {data.auto_blocks?.length || 0} option{(data.auto_blocks?.length || 0) === 1 ? '' : 's'}.</>}
                </p>
              )}
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {FileChips}
                <span className="text-[10px] text-indigo-600 font-semibold">
                  {isShort && filesList.length === 0 ? 'Show more →' : 'Tap to open →'}
                </span>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
          </div>
        </section>

        {modalOpen && (
          <InstructionsModal
            data={data}
            order={order}
            fileUrl={fileUrl}
            onClose={() => setModalOpen(false)}
          />
        )}
      </>
    );
  }

  // ─── ADMIN SURFACE (editor) ───────────────────────────────────
  const adminInner = (
    <>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-bold text-slate-800 text-sm flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-indigo-500" />
          Instructions for customer
        </h4>
        <span className="text-[10px] uppercase font-bold tracking-wide text-slate-400">
          OPTIONAL · shown alongside modified file
        </span>
      </div>
      <p className="text-[11px] text-slate-500 italic mb-2 leading-snug">
        Write a short note for the customer (where to flash, special notes).
        Leave empty to fall back to option descriptions + Image library photos
        automatically.
      </p>

      <textarea
        value={textDraft}
        onChange={(e) => setTextDraft(e.target.value)}
        onBlur={saveText}
        disabled={busy}
        placeholder="e.g. Flash via Alientech KESS3 only. After flashing, clear DTCs with VCDS."
        rows={3}
        className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none resize-y font-sans"
        data-testid="instructions-text"
      />
    </>
  );

  const adminFilesList = (
    <div className="mt-3 space-y-2">
      {(data?.files || []).map((f) => {
        const Icon = fileIcon(f.mime);
        return (
          <div
            key={f.id}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50"
            data-testid={`instruction-file-${f.id}`}
          >
            <Icon className="w-4 h-4 text-indigo-600 flex-shrink-0" />
            <a
              href={fileUrl(f.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 min-w-0 text-xs font-medium text-slate-800 hover:text-indigo-700 truncate"
            >
              {f.filename}
            </a>
            <span className="text-[10px] text-slate-500 tabular-nums">{bytesPretty(f.size)}</span>
            <button
              onClick={() => deleteFile(f.id, f.filename)}
              disabled={busy}
              title="Delete attachment"
              className="text-rose-600 hover:bg-rose-50 p-1 rounded"
              data-testid={`instruction-delete-${f.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}

      {(data?.files?.length || 0) < MAX_FILES && (
        <label className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer border-2 border-dashed ${
          busy ? 'border-slate-200 text-slate-400 cursor-wait' : 'border-indigo-300 text-indigo-700 hover:bg-indigo-50'
        }`} data-testid="instructions-upload-label">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {busy ? 'Uploading…' : `Attach PDF or image (${(data?.files?.length || 0)}/${MAX_FILES})`}
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={uploadFile}
            disabled={busy}
            data-testid="instructions-upload-input"
          />
        </label>
      )}
    </div>
  );

  if (embedded) {
    // Admin — collapsed by default. Strip shows current note preview
    // + attachment count; clicking expands the full editor.
    if (!adminExpanded) {
      const hasContent = !!((data?.text || '').trim() || (data?.files?.length || 0) > 0);
      const notePeek = (data?.text || '').trim();
      return (
        <button
          type="button"
          onClick={() => setAdminExpanded(true)}
          data-testid="instructions-admin-strip"
          className="mt-3 pt-3 border-t border-slate-200 w-full flex items-start gap-2.5 text-left group"
        >
          <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-sm flex-shrink-0">
            <BookOpen className="w-3.5 h-3.5 text-white" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-bold text-slate-800">Instructions for customer</span>
            <span className="block text-[11px] text-slate-500 truncate">
              {hasContent
                ? <>
                    {notePeek && <>&ldquo;{notePeek.slice(0, 70)}{notePeek.length > 70 ? '…' : ''}&rdquo; </>}
                    {(data?.files?.length || 0) > 0 && <>· {data.files.length} attachment{data.files.length > 1 ? 's' : ''}</>}
                  </>
                : 'Empty — auto-falls back to option descriptions + Photo Library.'}
              <span className="ml-1 text-indigo-600 font-semibold">Click to {hasContent ? 'edit' : 'add a note'}.</span>
            </span>
          </span>
          <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 group-hover:text-indigo-500 transition mt-1" />
        </button>
      );
    }
    return (
      <div className="mt-3 pt-3 border-t border-slate-200" data-testid="instructions-admin">
        <button
          type="button"
          onClick={() => setAdminExpanded(false)}
          className="text-[10px] text-slate-500 hover:text-indigo-600 font-semibold mb-2 inline-flex items-center gap-1"
          data-testid="instructions-admin-collapse"
        >
          ← Collapse
        </button>
        {adminInner}
        {adminFilesList}
      </div>
    );
  }

  // Non-embedded variant (used standalone outside the order drawer).
  if (!adminExpanded) {
    const hasContent = !!((data?.text || '').trim() || (data?.files?.length || 0) > 0);
    const notePeek = (data?.text || '').trim();
    return (
      <section className="bg-white rounded-xl border border-slate-200 mt-3 shadow-sm" data-testid="instructions-admin">
        <button
          type="button"
          onClick={() => setAdminExpanded(true)}
          data-testid="instructions-admin-strip"
          className="w-full flex items-start gap-2.5 text-left p-3 hover:bg-indigo-50/50 rounded-xl transition group"
        >
          <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-sm flex-shrink-0">
            <BookOpen className="w-4 h-4 text-white" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-bold text-slate-800">Instructions for customer</span>
            <span className="block text-[11px] text-slate-500 truncate">
              {hasContent
                ? <>
                    {notePeek && <>&ldquo;{notePeek.slice(0, 80)}{notePeek.length > 80 ? '…' : ''}&rdquo; </>}
                    {(data?.files?.length || 0) > 0 && <>· {data.files.length} attachment{data.files.length > 1 ? 's' : ''}</>}
                  </>
                : 'Empty — auto-falls back to option descriptions + Photo Library.'}
              <span className="ml-1 text-indigo-600 font-semibold">Click to {hasContent ? 'edit' : 'add a note'}.</span>
            </span>
          </span>
          <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 group-hover:text-indigo-500 transition mt-1" />
        </button>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-3.5 mt-3 shadow-sm" data-testid="instructions-admin">
      <button
        type="button"
        onClick={() => setAdminExpanded(false)}
        className="text-[10px] text-slate-500 hover:text-indigo-600 font-semibold mb-2 inline-flex items-center gap-1"
        data-testid="instructions-admin-collapse"
      >
        ← Collapse
      </button>
      {adminInner}
      {adminFilesList}
    </section>
  );
}


function InstructionsModal({ data, order, fileUrl, onClose }) {
  // Lock body scroll while modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onEsc);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8 bg-slate-900/50 backdrop-blur-md"
      onClick={onClose}
      data-testid="instructions-modal"
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b bg-gradient-to-r from-indigo-50 via-white to-cyan-50">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-sm">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900">Instructions for order {order.order_number}</h3>
            <p className="text-xs text-slate-500">
              {data.has_custom ? 'Delivered by operator' : 'Auto-generated from option catalogue'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-100"
            data-testid="instructions-modal-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {data.text && data.text.trim() && (
            <div className="prose prose-sm max-w-none">
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{data.text}</p>
            </div>
          )}

          {(data.files || []).map((f) => {
            const isPdf = f.mime === 'application/pdf';
            const isImg = (f.mime || '').startsWith('image/');
            return (
              <div key={f.id} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`instructions-modal-file-${f.id}`}>
                <div className="px-3 py-2 bg-slate-50 border-b flex items-center gap-2">
                  {isPdf ? <FileType2 className="w-4 h-4 text-rose-600" /> : <ImageIcon className="w-4 h-4 text-indigo-600" />}
                  <span className="text-xs font-medium text-slate-800 truncate flex-1">{f.filename}</span>
                  <a
                    href={fileUrl(f.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-indigo-700 hover:underline"
                  >
                    Open in new tab
                  </a>
                </div>
                {isImg && (
                  <img
                    src={fileUrl(f.id)}
                    alt={f.filename}
                    className="w-full object-contain max-h-[60vh] bg-slate-100"
                  />
                )}
                {isPdf && (
                  <iframe
                    src={fileUrl(f.id)}
                    title={f.filename}
                    className="w-full h-[60vh] bg-white"
                  />
                )}
              </div>
            );
          })}

          {/* Auto-fallback blocks (only when admin didn't customise) */}
          {!data.has_custom && (data.auto_blocks || []).map((b) => (
            <div key={b.code} className="border border-slate-200 rounded-xl p-4 bg-slate-50/50" data-testid={`instructions-auto-${b.code}`}>
              <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                <span className="px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide bg-indigo-100 text-indigo-700">
                  {b.code}
                </span>
                <span>{b.name}</span>
              </h4>
              {b.description && (
                <div
                  className="prose prose-sm max-w-none text-sm text-slate-700 mb-3"
                  dangerouslySetInnerHTML={{ __html: b.description }}
                />
              )}
              {b.photos && b.photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {b.photos.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                      <img src={url} alt={`${b.code} reference ${i + 1}`} className="w-full aspect-square object-cover rounded-lg border border-slate-200 hover:shadow-md transition" loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}

          {!data.has_custom && (data.auto_blocks || []).length === 0 && !data.text && (data.files || []).length === 0 && (
            <p className="text-sm text-slate-500 italic">No instructions for this order.</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
          <span>Press <kbd className="px-1 py-0.5 rounded bg-white border text-[10px] font-mono">Esc</kbd> to close.</span>
          <span className="font-mono">{order.order_number}</span>
        </div>
      </div>
    </div>
  );
}
